<template>
    <hts-drawer
        v-model="visible"
        :rules="rules"
        :submit="handleSubmit"
        :title="$t('labels.addLocation')"
        no-reset
        size="large"
        @close="closeOverSide">
        <hts-card :title="$t('labels.location')">
            <div class="grid gap-4 grid-cols-1 sm:grid-cols-2">
                <hts-form-input
                    v-model="formData.address"
                    :label="$t('labels.address')"
                    :required="true"
                    :color="color"
                    :disabled="true"
                    name="address"
                    prop="address" />
                <hts-form-input
                    v-model="formData.locationName"
                    :label="$t('labels.locationName')"
                    :required="true"
                    :color="color"
                    :disabled="!formData.newLocation"
                    name="locationName"
                    prop="locationName" />
                <hts-form-input
                    v-model="formData.latitudeAndLongitude"
                    :label="$t('labels.latitudeAndLongitude')"
                    :required="true"
                    :color="color"
                    :disabled="true"
                    name="latitudeAndLongitude"
                    prop="latitudeAndLongitude" />
            </div>
        </hts-card>

        <hts-card :title="$t('labels.position')">
            <hts-map-autocomplete
                v-model="findPosition"
                :label="$t('labels.findPosition')"
                :placeholder="$t('labels.nodeModule.search')"
                @get-place-detail="(data) => handleSetPosition(data)" />
            <hts-map-node
                class="min-h-[600px]"
                :center-position="dataPosition"
                center-marker
                :node-list="locationList"
                @click="getLocationMap" />
        </hts-card>
    </hts-drawer>
</template>
<script setup lang="ts">
const props = defineProps({
  color: {
    type: String
  }
})
const emits = defineEmits(['close', 'refreshTables', 'newLocation'])

const visible = ref(false)
const rules = {
  address: 'required',
  locationName: 'required',
  latitudeAndLongitude: 'required'
}

const cameraLocation = {
  lat: 10.7561542,
  lng: 106.6896784
}
const dataPosition = ref<{ lat: number; lng: number; placeId?: string }>({
  lat: 10.7561542,
  lng: 106.6896784
})

const formData = ref<{ [key: string]: any }>({
  address: '',
  locationName: '',
  placeId: '',
  latitudeAndLongitude: '',
  lat: 0,
  lng: 0,
  newLocation: false,
  addressDetail: {}
} as { [key: string]: any })

const locationList = ref<Array<{ [key: string]: any }>>([])
const findPosition = ref('')

const open = async () => {
  visible.value = true
  formData.value = {
    address: '',
    locationName: '',
    placeId: '',
    latitudeAndLongitude: '',
    lat: 0,
    lng: 0,
    newLocation: false
  }
  dataPosition.value = {
    lat: 10.7561542,
    lng: 106.6896784
  }
  findPosition.value = ''
  await getAllNode()
}

const resetFields = () => {
  formData.value = {
    address: '',
    locationName: '',
    placeId: '',
    latitudeAndLongitude: '',
    lat: 0,
    lng: 0,
    newLocation: false
  }

  dataPosition.value = {
    lat: 10.7561542,
    lng: 106.6896784,
    placeId: ''
  }
  findPosition.value = ''
}

const closeOverSide = () => {
  visible.value = false
}

const geocodeLocation = async (lat: number, lng: number) => {
  const geocoder = new google.maps.Geocoder()
  const { results } = await geocoder.geocode({ location: { lat, lng } })

  return results
}

const updateLocationData = async (data: any) => {
  const lat = data.geometry.location.lat()
  const lng = data.geometry.location.lng()
  const addressDetail = await geocodeLocation(lat, lng)

  formData.value = {
    address: data.formatted_address,
    addressDetail,
    locationName: data.name,
    placeId: data.place_id,
    latitudeAndLongitude: `${lat}, ${lng}`,
    lat,
    lng,
    newLocation:
      Array.isArray(data.address_components?.[0]?.types) &&
      (data.address_components?.[0]?.types.includes('street_address') || data.address_components?.[0]?.types.includes('premise'))
  }

  dataPosition.value = { lat, lng }
  findPosition.value = data.name
}

const handleSetPosition = async (data: any) => {
  if (data) { await updateLocationData(data) }
}

const getLocationMap = async (location: any, place: any) => {
  if (place) {
    await updateLocationData(place)
  } else if (location) {
    const { lat, lng } = location
    const results = await geocodeLocation(lat, lng)

    formData.value = {
      address: results[0]?.formatted_address || '',
      locationName: '',
      placeId: results[0]?.place_id || '',
      addressDetail: results,
      latitudeAndLongitude: `${lat}, ${lng}`,
      lat,
      lng,
      newLocation: true
    }

    dataPosition.value = { lat, lng }
    findPosition.value = results[0]?.formatted_address || ''
  }
}

const handleSubmit = async () => {
  const api = $urlAdmin.node.location.add
  const data: any = await $api(api, { body: formData.value })
  const resData = data?.data?.value
  if (resData.success) {
    process.client && $toast().success($t('labels.addLocationSuccess'))
    resetFields()
    closeOverSide()
    emits('newLocation', resData.data)
    emits('refreshTables')
  }
}

const getAllNode = async () => {
  const api = $urlAdmin.node.location.getAll
  const data: any = await $api(api)
  const resData = data.data.value

  if (resData?.success) {
    const list = resData.data || []

    locationList.value = list.map((item: any) => ({
      position: {
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lng)
      },
      isNewLocation: item.newLocation,
      icon: item.newLocation ? '/assets/new-location.png' : '/assets/location.png',
      title: item.locationName,
      placeId: item.placeId
    }))
  }
}

defineExpose({
  open
})
</script>
